<template lang="pug">
el-form-item.admin-form-field-group(
  :id="fieldElementId",
  :class="fieldClasses",
  :style="fieldStyle",
  :label="label",
  :prop="prop",
  :required="elFormItemRequired",
  :error="fieldError || undefined"
)
  template(#label)
    span.admin-form-field-group__label(aria-hidden="true") {{ label }}
  fieldset.admin-form-field-group__fieldset(:disabled="fieldDisabled")
    legend.admin-form-field-group__legend {{ label }}
    p.admin-form-field-group__help(v-if="help", :id="helpId") {{ help }}
    slot(
      :aria-describedby="ariaDescribedby",
      :aria-invalid="Boolean(fieldError)",
      :aria-required="required",
      :disabled="fieldDisabled"
    )
  template(#error="{ error }")
    .el-form-item__error(:id="errorId") {{ error }}
</template>

<script setup lang="ts">
import { computed, inject } from "vue";
import { adminFormContextKey } from "./types";

const props = withDefaults(
  defineProps<{
    label: string;
    prop?: string;
    required?: boolean;
    allowNull?: boolean;
    help?: string;
    error?: string;
    disabled?: boolean;
    span?: "default" | "full";
    fieldClass?: string;
  }>(),
  {
    prop: undefined,
    required: false,
    allowNull: false,
    help: undefined,
    error: undefined,
    disabled: false,
    span: "default",
    fieldClass: undefined,
  },
);

const formContext = inject(adminFormContextKey, undefined);
const fieldError = computed(
  () =>
    props.error ??
    (props.prop ? formContext?.externalErrors.value[props.prop] : undefined),
);
const fieldDisabled = computed(
  () => props.disabled || formContext?.disabled.value === true,
);
// Element Plus adds its own required rule when this is true. That rule rejects
// null, even when the configured registration field explicitly permits null.
const elFormItemRequired = computed(() => props.required && !props.allowNull);
const fieldElementId = computed(() =>
  props.prop
    ? `admin-form-field-${props.prop.replaceAll(/[^a-zA-Z0-9_-]/g, "-")}`
    : undefined,
);
const helpId = computed(() =>
  props.prop ? `${fieldElementId.value}-help` : undefined,
);
const errorId = computed(() =>
  props.prop ? `${fieldElementId.value}-error` : undefined,
);
const ariaDescribedby = computed(
  () =>
    [helpId.value, fieldError.value ? errorId.value : undefined]
      .filter((id): id is string => id !== undefined)
      .join(" ") || undefined,
);
const fieldClasses = computed(() => [
  props.fieldClass,
  { "is-required": props.required },
]);
const fieldStyle = computed(() =>
  props.span === "full" ? { gridColumn: "1 / -1" } : undefined,
);
</script>

<style scoped>
.admin-form-field-group {
  align-content: start;
  margin-bottom: 0;
  min-width: 0;
}

.admin-form-field-group__fieldset {
  border: 0;
  margin: 0;
  min-inline-size: 0;
  padding: 0;
  width: 100%;
}

.admin-form-field-group__legend {
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
}

.admin-form-field-group :deep(.el-form-item__label) {
  align-items: flex-start;
  height: auto;
}

.admin-form-field-group :deep(.admin-form-field-group__label) {
  align-items: center;
  display: flex;
  min-height: 40px;
}

.admin-form-field-group__help {
  color: var(--el-text-color-secondary);
  font-size: var(--el-font-size-small);
  margin: 0 0 0.5rem;
}

.admin-form-field-group :deep(.el-form-item__content) {
  flex-wrap: wrap;
}

.admin-form-field-group :deep(.el-form-item__error) {
  box-sizing: border-box;
  contain: inline-size;
  flex-basis: 100%;
  max-inline-size: 100%;
  min-inline-size: 0;
  overflow-wrap: anywhere;
  padding-top: 0.25rem;
  position: static;
}
</style>
